import { Box, Typography, useTheme, MenuItem, Select, FormControl } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";  
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import Header from "../../components/Header";
import { useState, useEffect } from "react";

const Team = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null); 
  const [error, setError] = useState(null);  // Error state for handling errors
  const JWT_TOKEN = localStorage.getItem("token");

  // Fetch users data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("https://api.dmaverix.com/api/v2/auth/get-all-users", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${JWT_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        const result = await response.json();
        
        if (result.success) {
          const formattedData = result.data.map((user, index) => ({
            id: index + 1,
            username: user.username,
            email: user.email,
            name: user.name,
            isVerified: user.isVerified,
            role: user.role,
          }));
          setUsersData(formattedData);
        } else {
          console.error('Failed to fetch data:', result.message);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); 

  // Function to handle the update
  const handleUpdateUser = async (id, updatedData) => {
    try {
      const response = await fetch("https://api.dmaverix.com/api/v2/auth/user/update", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${JWT_TOKEN}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedData),
      });

      const result = await response.json();

      if (result.success) {
        // Update the local state with the updated user data
        const updatedUsers = usersData.map((user) => 
          user.id === id ? { ...user, ...updatedData } : user
        );
        setUsersData(updatedUsers);
        setEditingRole(null); // Close the dropdown after saving
        alert("User updated successfully!");
      } else {
        setError(result.message || "Failed to update user.");
      }
    } catch (error) {
      setError("Error updating user. Please try again.");
      console.error("Error updating user:", error);
    }
  };

  // Define columns for DataGrid
  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "username", headerName: "Username", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "isVerified",
      headerName: "Verified",
      flex: 1,
      renderCell: (params) => {  
        const { isVerified, id, username, role } = params.row;  
        return (
          <FormControl fullWidth>
            <Select
              value={isVerified ? "Verified" : "Not Verified"}
              onChange={(e) => {
                const updatedData = { 
                  username,  
                  role,  // Preserve the role
                  isVerified: e.target.value === "Verified"
                };
                handleUpdateUser(id, updatedData);  // Call the update API
              }}
              label="Verified"
            >
              <MenuItem value="Verified">Verified</MenuItem>
              <MenuItem value="Not Verified">Not Verified</MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: (params) => {
        const { id, username, role, isVerified } = params.row;

        // Handle editing the role (show dropdown if this row is being edited)
        if (editingRole === id) {
          return (
            <FormControl fullWidth>
              <Select
                value={role}
                onChange={(e) => {
                  const updatedData = {
                    username,  
                    isVerified,  
                    role: e.target.value
                  };
                  handleUpdateUser(id, updatedData);  // Call the update API for role
                }}
                label="Role"
                autoWidth
                sx={{width:'290px'}}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="owner">Owner</MenuItem>
              </Select>
            </FormControl>
          );
        }

        // Default display of the role with icon and background color
        let icon;
        switch (role) {
          case "admin":
            icon = <AdminPanelSettingsOutlinedIcon />;
            break;
          case "user":
            icon = <LockOpenOutlinedIcon />;
            break;
          case "owner":
            icon = <SecurityOutlinedIcon />;
            break;
          default:
            icon = null;
        }

        return (
          <Box
            width="60%"
            m="0 auto"
            p="5px"
            display="flex"
            borderBottom="1px solid lightgray"
            justifyContent="center"
            backgroundColor={
              role === "admin"
                ? colors.greenAccent?.[600] || '#28a745'
                : role === "user"
                ? colors.blueAccent?.[700] || '#007bff'
                : colors.purple?.[500] || '#6f42c1'
            }
            borderRadius="4px"
            onClick={() => setEditingRole(id)} // Enable editing when clicked
          >
            {icon}
            <Typography color={colors.grey?.[100] || '#f8f9fa'} sx={{ ml: "5px" }}>
              {role}
            </Typography>
          </Box>
        );
      },
    },
  ];

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box m="20px">
      <Header title="ROLE" subtitle="Managing the Role" />
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            "& .MuiDataGrid-row": {
              minHeight: "70px",
              marginTop:'10px',
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            padding: "8px 16px",
          },
          "& .name-column--cell": {
            color: colors.greenAccent?.[300] || '#28a745',
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent?.[700] || '#007bff',
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary?.[400] || '#343a40',
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent?.[700] || '#007bff',
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent?.[200]} !important` || '#28a745 !important',
          },
        }}
      >
        <DataGrid checkboxSelection rows={usersData} columns={columns} />
      </Box>
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default Team;
